import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';
import { Link } from 'react-router-dom/cjs/react-router-dom';

import { Button } from 'ui-components/components/button';
import { FlexColumn, FlexRow, Gap } from 'ui-components/components/flex';
import { ResponsiveLayout, ResponsiveWidth } from 'ui-components/components/responsive-layout';
import { Text } from 'ui-components/components/text';
import { TextType } from 'ui-components/components/text/text-type';

import style from './style.module.less';
import UserDateTimezoneFormat from '../../../components/user-date-timezone-format';
import Global from '../../../store/global-selectors';
import { SettingsPageTitle } from '../components/settings-page-title/settings-page-title';
import { pages } from '../nav-items';
import PaymentAndSubscriptionHelpArticleLink from '../payment-and-subscription/components/help-article-link';
import { useDeriveSubscriptionInfo } from '../payment-and-subscription/hooks/use-derive-subscription-info';
import messages from '../payment-and-subscription/page/messages';
import CancellationEntryPoint from '../payment-and-subscription/sections/cancellation/entry-point';

const pathToPastInvoices = `/${pages.settings.location}/${pages.paymentAndSubscription.location}/${pages.invoices.location}`;

export const SubscriptionPageWrapper = React.forwardRef(({ children, reactivatePlan }, ref) => {
  const isForV2 = useRouteMatch('/v2Subscriptions');
  const planInfo = useSelector(Global.selectSellerPlanInfo);
  const { isPlanLocked, billingCycleEndDateTimeUTC } = planInfo;

  const { hasActivePaidPlan, planWillExpire } = useDeriveSubscriptionInfo(planInfo);
  const shouldShowCancelEntryPoint = hasActivePaidPlan && !planWillExpire;
  const shouldShowUndoCancellation = planWillExpire;
  const shouldShowDivider = !isPlanLocked && (shouldShowCancelEntryPoint || shouldShowUndoCancellation);
  return (
    <ResponsiveLayout className={style.content}>
      <FlexColumn ref={ref} gap={Gap.Space32} responsiveWidth={ResponsiveWidth.Cols12}>
        <FlexColumn gap={Gap.Space8} responsiveWidth={ResponsiveWidth.Cols12}>
          <FlexRow className={style['subscription-header']} responsiveWidth={ResponsiveWidth.Cols12}>
            <>
              <div>
                <SettingsPageTitle value={messages.header} />
                <Text textType={TextType.Paragraph} value={messages.subDescription} />
              </div>
              {!isForV2 && (
                <div className={style['header-right']}>
                  {!isPlanLocked && (
                    <Link className={style['past-invoices-link']} to={pathToPastInvoices}>
                      <Text textType={TextType.Caption} value={messages.pastInvoices} />
                    </Link>
                  )}
                  {shouldShowDivider ? <>|</> : null}
                  {shouldShowCancelEntryPoint ? <CancellationEntryPoint inTrial={false} /> : null}
                  {shouldShowUndoCancellation ? (
                    <Button onClick={reactivatePlan} theme='link'>
                      {isPlanLocked ? (
                        <Text
                          formattedMessageValues={{
                            endDate: <UserDateTimezoneFormat dateTime={billingCycleEndDateTimeUTC} />,
                          }}
                          textType={TextType.Caption}
                          value={messages.undoCtpCancellation}
                        />
                      ) : (
                        <Text textType={TextType.Caption} value={messages.undoCancellation} />
                      )}
                    </Button>
                  ) : null}
                </div>
              )}
            </>
          </FlexRow>
          <PaymentAndSubscriptionHelpArticleLink />
        </FlexColumn>
        {children}
      </FlexColumn>
    </ResponsiveLayout>
  );
});

SubscriptionPageWrapper.propTypes = {
  children: PropTypes.node.isRequired,
  reactivatePlan: PropTypes.func.isRequired,
};
